import { memo, useState } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';
import Collapse from '../Collapse';

const CARD_BASE = 'https://wver.sprintstaticdata.com/v230/static/front/img/cards';

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}

// A locked cell WITH a liability pins its padlock to the TOP (lock-top) so the
// figure stays readable; without one the padlock stays centred (user
// 2026-10-14) — the same rule as teen20b.
function suspCls(item, book) {
  if (!isSusp(item)) return '';
  return Number(book) ? ' suspended lock-top' : ' suspended';
}

function formatAmount(n) {
  const num = Number(n) || 0;
  if (num >= 100000) return `${num / 100000}L`;
  if (num >= 1000) return `${num / 1000}K`;
  return String(num);
}

function bookOf(exposures, item) {
  const v = exposures?.[`sid:${item?.sid}`] ?? exposures?.[item?.nat];
  return v ? roundBook(v) : 0;
}

function bookCls(v) {
  if (!v) return 'book-black';
  return v < 0 ? 'book-red' : 'book-green';
}

// 20-20 Teenpatti C (teen20c) — admin reference: teen20casino-container
// d-none-small; A/B sides me title row (Player / 3 Baccarat / Total /
// Pair Plus + positional range ids), odds row (Pair Plus odds ki jagah
// "A"/"B" letter), phir Black/Red rb-boxes. Range ids POSITIONAL hain
// (A side 1-6, B side 7-12), sid se nahi. Sab selections Fancy (singles).
const BetTableTeen20c = memo(function BetTableTeen20c({ gameData, onBet, exposures }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});
  const [openRanges, setOpenRanges] = useState({});
  const toggle = (id) => setOpenRanges((p) => ({ ...p, [id]: !p[id] }));
  const bySid = (sid) => sub.find((s) => Number(s.sid) === sid);

  // range icon (float-right, bina collapsed/aria) + collapse icon-range
  const rangeInfo = (pos, item, fallbackMax) => {
    const rid = `range${pos}`;
    return (
      <>
        <i
          data-toggle="collapse"
          data-target={`#${rid}`}
          className="fas fa-info-circle float-right"
          onClick={() => toggle(rid)}
          style={{ cursor: 'pointer' }}
        ></i>
        {' '}
        <Collapse id={rid} open={openRanges[rid]} className="icon-range">
          {'\n                          R:'}
          <span>{formatAmount(item?.min ?? 100)}</span>
          -
          <span>{formatAmount(item?.max ?? fallbackMax)}</span>
        </Collapse>
      </>
    );
  };

  const oddsBox = (item, display) => (
    <div
      className={`back casino-bl-box-item${suspCls(item, bookOf(exposures, item))}`}
      onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
    >
      <span className="casino-box-odd">{display ?? (item?.b || 0)}</span>
      {' '}
      <span className={`casino-book ${bookCls(bookOf(exposures, item))}`}>{fmtBook(bookOf(exposures, item))}</span>
    </div>
  );

  // Desktop card-set (Black / Red) boxes keep the padlock CENTRED even with a
  // liability (user 2026-10-14) — no book argument, so never lock-top.
  const rbBox = (item, pos, black) => (
    <div className={`casino-rb-box ${black ? 'blackcontainer' : 'redcontainer'}`}>
      <div
        className={`casino-rb-box-player ${black ? 'blackbox' : 'redbox'} back${suspCls(item)}`}
        onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
      >
        {/* Reference: only Player A's (pos 5) black box right-aligns its suit icons. */}
        <div className={black && pos === 5 ? 'text-right' : undefined}>
          {black ? (
            <>
              <img src={`${CARD_BASE}/spade.png`} />
              {' '}
              <img src={`${CARD_BASE}/club.png`} />
            </>
          ) : (
            <>
              <img src={`${CARD_BASE}/heart.png`} />
              {' '}
              <img src={`${CARD_BASE}/diamond.png`} className="diamond-icon" />
            </>
          )}
        </div>
        {' '}
        <div className="text-right">
          <span className="d-block casino-box-odd">{item?.b || 0}</span>
          {' '}
          <span className={`casino-book ${bookCls(bookOf(exposures, item))}`}>{fmtBook(bookOf(exposures, item))}</span>
        </div>
      </div>
      {' '}
      <div className="text-right casino-rb-box-player-range">
        {rangeInfo(pos, item, 25000)}
      </div>
    </div>
  );

  // side: A -> sids [player 1, bacc 5, total 11, pair 3, black 7, red 8],
  //       B -> sids [2, 6, 12, 4, 9, 10]; positions A 1-6, B 7-12
  const side = (label, playerCls, sids, basePos, pairLetter) => {
    const [pSid, baccSid, totalSid, pairSid, blackSid, redSid] = sids;
    const player = bySid(pSid);
    const bacc = bySid(baccSid);
    const total = bySid(totalSid);
    const pair = bySid(pairSid);
    const black = bySid(blackSid);
    const red = bySid(redSid);
    return (
      <>
        <div className="casino-box-row">
          <div className="casino-nation-name no-border casino-bl-box-title">
            <div className={playerCls}>{label}</div>
          </div>
        </div>
        {' '}
        <div className="casino-bl-box casino-bl-box-title">
          <div className="casino-bl-box-item">
            <span>Player</span>
            {' '}
            <div className="ml-1">{rangeInfo(basePos, player, 500000)}</div>
          </div>
          {' '}
          <div className="casino-bl-box-item">
            <span>3 Baccarat</span>
            {' '}
            <div className="ml-1">{rangeInfo(basePos + 1, bacc, 100000)}</div>
          </div>
          {' '}
          <div className="casino-bl-box-item">
            <span>Total</span>
            {' '}
            <div className="ml-1">{rangeInfo(basePos + 2, total, 100000)}</div>
          </div>
          {' '}
          <div className="casino-bl-box-item">
            <span>Pair Plus</span>
            {' '}
            <div className="ml-1">{rangeInfo(basePos + 3, pair, 25000)}</div>
          </div>
        </div>
        {' '}
        <div className="casino-bl-box mb-4">
          {oddsBox(player)}
          {' '}
          {oddsBox(bacc)}
          {' '}
          {oddsBox(total)}
          {' '}
          {oddsBox(pair, pairLetter)}
        </div>
        {' '}
        <div className="casino-rb-box-container mb-3">
          {rbBox(black, basePos + 4, true)}
          {' '}
          {rbBox(red, basePos + 5, false)}
        </div>
      </>
    );
  };

  // book span (mobile) — book-black without casino-book prefix (reference)
  const mobileOddsBox = (item, display) => (
    <div
      className={`back casino-bl-box-item${suspCls(item, bookOf(exposures, item))}`}
      onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
    >
      <span className="casino-box-odd">{display ?? (item?.b || 0)}</span>
      {' '}
      <span className={bookCls(bookOf(exposures, item))}>{fmtBook(bookOf(exposures, item))}</span>
    </div>
  );

  // mobile rows: label/suits + A/B sids + range pos + max. Pair Plus odds = A/B letter.
  const MOBILE_ROWS = [
    { label: 'Player', aSid: 1, bSid: 2, pos: 1, max: 500000 },
    { label: '3 Baccarat', aSid: 5, bSid: 6, pos: 2, max: 100000 },
    { label: 'Total', aSid: 11, bSid: 12, pos: 3, max: 100000 },
    { label: 'Pair Plus', aSid: 3, bSid: 4, pos: 4, max: 25000, dispA: 'A', dispB: 'B' },
    { suit: 'black', aSid: 7, bSid: 9, pos: 5, max: 25000 },
    { suit: 'red', aSid: 8, bSid: 10, pos: 6, max: 25000 },
  ];

  const mobileTable = (
    <div className="teen20casino-container d-none-big">
      <div className="casino-bl-box casino-bl-box-title">
        <div className="casino-bl-box-item"></div>
        {' '}
        <div className="casino-bl-box-item playera">Player A</div>
        {' '}
        <div className="casino-bl-box-item playerb">Player B</div>
      </div>
      {' '}
      {MOBILE_ROWS.map((row) => {
        const a = bySid(row.aSid);
        const b = bySid(row.bSid);
        return (
          <div className="casino-bl-box" key={row.pos}>
            <div className="casino-bl-box-item casino-odds-name casino-nation-name">
              {row.suit ? (
                <div>
                  {row.suit === 'black' ? (
                    <><img src={`${CARD_BASE}/spade.png`} /> <img src={`${CARD_BASE}/club.png`} /></>
                  ) : (
                    <><img src={`${CARD_BASE}/heart.png`} /> <img src={`${CARD_BASE}/diamond.png`} /></>
                  )}
                </div>
              ) : (
                <span>{row.label}</span>
              )}
              {' '}
              {rangeInfo(row.pos, a, row.max)}
            </div>
            {' '}
            {mobileOddsBox(a, row.dispA)}
            {' '}
            {mobileOddsBox(b, row.dispB)}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {mobileTable}
      {' '}
      <div className="teen20casino-container d-none-small">
        <div className="teen20left">
          {side('Player A', 'playera', [1, 5, 11, 3, 7, 8], 1, 'A')}
        </div>
        {' '}
        <div className="teen20center"></div>
        {' '}
        <div className="teen20right">
          {side('Player B', 'playerb', [2, 6, 12, 4, 9, 10], 7, 'B')}
        </div>
      </div>
    </>
  );
});

export default BetTableTeen20c;
